import { useState, useEffect, useCallback } from 'react';
import styles from './NotasEmitidas.module.css';

const API_URL = process.env.REACT_APP_API_URL;

const ROTULO_STATUS = {
  processando: 'Processando',
  autorizada: 'Autorizada',
  rejeitada: 'Rejeitada',
  cancelada: 'Cancelada',
  erro: 'Sem resposta'
};

function mesAtual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function cabecalhos() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const dinheiro = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

// Lista das NFC-e do mês, com resumo, notas que precisam de atenção e o
// download do .zip de XML que o contador pede todo mês.
function NotasEmitidas({ estabelecimentoId }) {
  const [mes, setMes] = useState(mesAtual());
  const [status, setStatus] = useState('');
  const [dados, setDados] = useState({ notas: [], resumo: null });
  const [carregando, setCarregando] = useState(true);
  const [baixando, setBaixando] = useState(false);

  const carregar = useCallback(async () => {
    if (!estabelecimentoId) return;
    setCarregando(true);
    try {
      const params = new URLSearchParams({ id_estabelecimento: estabelecimentoId, mes });
      if (status) params.set('status', status);
      const res = await fetch(`${API_URL}/fiscal/notas?${params}`, { headers: cabecalhos() });
      const corpo = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(corpo.error || 'Falha ao buscar as notas');
      setDados(corpo);
    } catch (error) {
      alert('Erro ao carregar as notas: ' + error.message);
    } finally {
      setCarregando(false);
    }
  }, [estabelecimentoId, mes, status]);

  useEffect(() => { carregar(); }, [carregar]);

  const baixarZip = async () => {
    setBaixando(true);
    try {
      const res = await fetch(`${API_URL}/fiscal/notas/exportar?id_estabelecimento=${estabelecimentoId}&mes=${mes}`, { headers: cabecalhos() });
      if (!res.ok) {
        const corpo = await res.json().catch(() => ({}));
        throw new Error(corpo.error || 'Falha ao gerar o arquivo');
      }
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = `nfce-${mes}.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.message);
    } finally {
      setBaixando(false);
    }
  };

  const resumo = dados.resumo;
  const precisamAtencao = resumo ? resumo.rejeitada + resumo.erro + resumo.processando : 0;

  return (
    <section className={styles.painel}>
      <strong className={styles.painelTitulo}>Notas emitidas</strong>

      {precisamAtencao > 0 && (
        <div className={styles.atencao}>
          {precisamAtencao} nota(s) rejeitada(s), sem resposta ou ainda processando neste mês — abra o pedido para ver o motivo e tentar de novo.
        </div>
      )}

      <div className={styles.filtros}>
        <label className={styles.campo}>
          <span className={styles.campoRotulo}>Mês</span>
          <input className={styles.input} type="month" value={mes} onChange={(e) => setMes(e.target.value || mesAtual())} />
        </label>
        <label className={styles.campo}>
          <span className={styles.campoRotulo}>Situação</span>
          <select className={styles.input} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todas</option>
            {Object.entries(ROTULO_STATUS).map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
          </select>
        </label>
        <button className={styles.btnSecundario} onClick={carregar} disabled={carregando}>Atualizar</button>
        <button className={styles.btnPrimario} onClick={baixarZip} disabled={baixando || !resumo || resumo.autorizada + resumo.cancelada === 0}>
          {baixando ? 'Gerando...' : 'Baixar XML do mês (.zip)'}
        </button>
      </div>

      {resumo && (
        <div className={styles.resumo}>
          <span><b>{resumo.autorizada}</b> autorizada(s) · {dinheiro(resumo.valor_autorizado)}</span>
          <span><b>{resumo.cancelada}</b> cancelada(s)</span>
          <span><b>{resumo.rejeitada}</b> rejeitada(s)</span>
          <span><b>{resumo.erro + resumo.processando}</b> pendente(s)</span>
        </div>
      )}

      <div className={styles.tabelaWrapper}>
        {carregando ? (
          <p className={styles.mensagemVazia}>Carregando...</p>
        ) : dados.notas.length === 0 ? (
          <p className={styles.mensagemVazia}>Nenhuma nota neste período.</p>
        ) : (
          <table className={styles.tabela}>
            <thead>
              <tr><th>Pedido</th><th>Nota</th><th>Situação</th><th>Valor</th><th>Data</th><th>Detalhe</th><th></th></tr>
            </thead>
            <tbody>
              {dados.notas.map((nota) => (
                <tr key={nota.id}>
                  <td data-label="Pedido">#{nota.numero_pedido ?? nota.id_pedido}</td>
                  <td data-label="Nota">{nota.numero ? `${nota.numero} / ${nota.serie}` : '—'}</td>
                  <td data-label="Situação"><span className={`${styles.selo} ${styles[nota.status]}`}>{ROTULO_STATUS[nota.status] || nota.status}</span></td>
                  <td data-label="Valor">{dinheiro(nota.valor_total)}</td>
                  <td data-label="Data" className={styles.colunaMuted}>{new Date(nota.created_at).toLocaleString('pt-BR')}</td>
                  <td data-label="Detalhe" className={styles.colunaDetalhe}>{nota.status !== 'autorizada' ? nota.mensagem || '' : ''}</td>
                  <td className={styles.colunaLinks}>
                    {nota.url_danfe && <a href={nota.url_danfe} target="_blank" rel="noreferrer">Cupom</a>}
                    {nota.url_xml && <a href={nota.url_xml} target="_blank" rel="noreferrer">XML</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export default NotasEmitidas;
